import React, { useEffect, useState } from "react";
import { FaPlusCircle, FaMinusCircle, FaExclamationTriangle } from "react-icons/fa";
import Swal from "sweetalert2";
import { cpfMask, datetimeToDateBR } from "../../functions/mask";
import { floatToPrice } from "../../functions/price_functions";
import "../vendorOrderItem/index.css";
import "./CustomerCartItem.css";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import moment from "moment";


// Tipagem corrigida baseada na estrutura real dos dados
export type CartProduct = {
    id: number;
    img: string;
    sku: string;
    variation?: Array<{
        grid: string;
        valor: string;
    }>;
    name: string;
    price: number;
    unitPrice: number;
    discount: number;
    discountGeral: number;
    promotional_price: number;
    promotionalPrice: number;
    quantity: number;
    code: string;
    sku_info?: Array<{
        vendas: number | null;
        inventory: number;
        minimum_stock: number | null;
    }>;
};

export type CartData = {
    products: CartProduct[];
    totalDiscount?: number;
} | CartProduct[];

export type VendorCartItem = {
    id: number;
    cpfCustomer: string;
    customerName: string;
    phoneCustomer: string;
    cpfSeller: string;
    pluginspaceId: number;
    locationId: number;
    jsonCart: CartData;
    status: string;
    created: string;
    updated: string;
    canceled: string | null;
    urlcart: string;
    guid: string;
    order: string;
};

interface CustomerCartItemProps {
    cart: VendorCartItem;
    colorStatus?: string;
    colorActiveStatus?: string;
    fontStatus?: string;
    onUseCart?: (cartGuid: string, urlCart: string) => void;
}

export function CustomerCartItem({
    cart,
    colorStatus,
    colorActiveStatus,
    fontStatus,
    onUseCart
}: CustomerCartItemProps) {
    const [extended, setExtended] = useState(false);
    const [isCartValid, setIsCartValid] = useState<boolean | null>(null);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const { getAppConfig } = useAppConfig();
    const { getThisPluginspace } = usePluginspace();

    useEffect(() => {
        if (cart.status === "open" && extended) {
            validateCart(cart.created).then(result => {
                setIsCartValid(result.valid);
                setValidationMessage(result.msg);
            });
        }
    }, [extended, cart.status, cart.created]);



    const getProducts = (): CartProduct[] => {
        if (Array.isArray(cart.jsonCart)) {
            return cart.jsonCart;
        } else if (cart.jsonCart && typeof cart.jsonCart === 'object' && 'products' in cart.jsonCart) {
            return cart.jsonCart.products;
        }
        return [];
    };

    const calculateTotal = (produtos: CartProduct[]) => {
        let total = 0;
        for (const product of produtos) {
            // Sempre usa o preço original para o cálculo base
            const originalPrice = product.price || product.unitPrice;
            total += originalPrice * product.quantity;
        }
        return total;
    };

    const calculateTotalDiscount = (produtos: CartProduct[]) => {
        let total = 0;
        for (const product of produtos) {
            const originalPrice = product.price || product.unitPrice;
            const finalPrice = product.promotional_price || product.promotionalPrice || originalPrice;

            // Desconto = diferença entre preço original e preço final
            const productDiscount = originalPrice - finalPrice;
            total += productDiscount * product.quantity;



        }
        return total;
    };

    const getCartDiscount = () => {
        // Verifica se existe desconto geral no carrinho
        if (cart.jsonCart && typeof cart.jsonCart === 'object' && 'totalDiscount' in cart.jsonCart) {
            return cart.jsonCart.totalDiscount || 0;
        }
        return 0;
    };

    const status = () => {
        if (cart.status === "canceled") {
            return "rejected";
        }
        if (cart.status === "open") {
            return "pending";
        }
        return "confirmed";
    };

    const statusToPortugues = () => {
        if (cart.status === "canceled") {
            return "Cancelado";
        }
        if (cart.status === "open") {
            return "Aberto";
        }
        return "Fechado";
    };

    function calcValueProduct(product: CartProduct) {
        const price = product.promotional_price || product.price || product.unitPrice;
        return floatToPrice(price * product.quantity);
    }

    function customerName() {
        return cart.customerName || "Cliente não encontrado";
    }

    const handleUseCart = () => {
        if (cart.status !== "open") {
            Swal.fire({
                icon: "warning",
                title: "Atenção!",
                text: "Este carrinho não está disponível para uso. Apenas carrinhos com status 'Aberto' podem ser utilizados.",
                confirmButtonText: "OK"
            });
            return;
        }

        Swal.fire({
            icon: "question",
            title: "Utilizar Carrinho",
            text: "Ao utilizar este carrinho, seu carrinho atual será substituído. Deseja continuar?",
            showCancelButton: true,
            confirmButtonText: "Sim, utilizar",
            cancelButtonText: "Cancelar"
        }).then((result) => {
            if (result.isConfirmed && onUseCart) {
                onUseCart(cart.guid, cart.urlcart || "");
            }
        });
    };

    const getCartDeliveryMethod = () => {
        if (cart.jsonCart && typeof cart.jsonCart === 'object' && 'type' in cart.jsonCart && 'freight' in cart.jsonCart) {

            return `${cart.jsonCart.type === "delivery" ? "Entrega" : "Retirada"}`;
        }
        return null;
    }

    const getCartFreight = () => {
        if (cart.jsonCart && typeof cart.jsonCart === 'object' && 'freight' in cart.jsonCart) {
            const freight: string = (cart.jsonCart.freight as string) ?? `0`;
            return parseFloat(freight);
        }
        return parseFloat(`0`);
    }

    async function validateCart(timeCart: string): Promise<{
        valid: boolean;
        msg: string;
    }> {
        try {
            let valid = false;
            const configLocal = await getAppConfig();
            const pg = await getThisPluginspace();

            const gtmValue =
                !pg || !pg?.gmt?.split(":")[0] ? -3 : parseInt(pg.gmt.split(":")[0]);

            const carrinhoDataCriacao = moment
                .utc(timeCart)
                .utcOffset(gtmValue)
                .seconds(0);
            const now = moment().utcOffset(gtmValue).seconds(0);

            const hour = configLocal
                ? configLocal.content.pdv.sellerCartExpirationTime.split(":")[0]
                : "2";
            const minute = configLocal
                ? configLocal?.content.pdv.sellerCartExpirationTime.split(":")[1]
                : "0";

            const validTimeCart = carrinhoDataCriacao
                .clone()
                .add(parseInt(hour), "hours")
                .add(parseInt(minute), "minutes");

            valid = validTimeCart.isAfter(now) || validTimeCart.isSame(now, "second");
            const timeDifference = now.diff(validTimeCart, "minutes");
            const hours = Math.floor(timeDifference / 60);
            const minutes = timeDifference % 60;

            let msg = "";

            if (hours && minutes) {
                msg += `${hours} hora(s) e ${minutes} minuto(s)`;
            } else if (hours) {
                msg += `${hours} hora(s)`;
            } else if (minutes) {
                msg += `${minutes} minuto(s)`;
            }

            return {
                valid: valid,
                msg: msg,
            };
        } catch (er) {
            return {
                valid: false,
                msg: ``,
            };
        }
    }

    const products = getProducts();

    const isExpired = cart.status === "open" && isCartValid === false;

    return (
        <div className="order-finded-vendor">
            <div
                className="number-order status-order"
                onClick={() => {
                    setExtended(!extended);
                }}
            >
                <div className="clientName">
                    <span className="code">Cliente: {customerName()}</span>
                </div>
                <div className="status">
                    <p className={`${status()}`}>{statusToPortugues()}</p>
                    {extended ? (
                        <FaMinusCircle color="#636363" />
                    ) : (
                        <FaPlusCircle color="#636363" />
                    )}
                </div>
            </div>
            {extended && (
                <div className="order-content">
                    <div id="resumoCompra" className="resumoCompra">
                        <div className="resumoCompraTitulo">
                            <h3>Resumo da Compra</h3>
                        </div>
                        <div id="itensCompra" className="itensCompra">
                            <div className="orderInfo">
                                <div className="pedido">
                                    <span>Nº do Carrinho </span>
                                    <span>#{cart.id}</span>
                                </div>
                                <div className="pedido">
                                    <span>Status do Carrinho </span>
                                    <span
                                        style={{
                                            background: cart.status === "open"
                                                ? ("#6c757d")
                                                : ("#28a745"),
                                            color: fontStatus || "#fff",
                                            boxSizing: "content-box",
                                            paddingLeft: "10px",
                                            paddingRight: "10px",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        {statusToPortugues()}
                                    </span>
                                </div>
                            </div>
                            {products.map((produto: CartProduct, index: number) => {
                                const hasPromotion = produto.promotional_price && produto.promotional_price < (produto.price || produto.unitPrice);
                                const originalPrice = produto.price || produto.unitPrice;
                                const finalPrice = produto.promotional_price || originalPrice;
                                const productDiscount = (originalPrice - finalPrice) * produto.quantity;
                                const generalDiscount = produto.discountGeral ? produto.discountGeral * produto.quantity : 0;
                                const totalProductDiscount = productDiscount + generalDiscount;

                                return (
                                    <div key={index} className="produto">
                                        <span className="productName">
                                            {produto.name} ({produto.quantity}x) <br />
                                            {`SKU: ${produto.sku}`}
                                            {produto.variation && produto.variation.length > 0 && (
                                                <>
                                                    <br />
                                                    {produto.variation.map((v, i) => (
                                                        <span key={i} style={{ fontSize: "12px", color: "#666" }}>
                                                            {v.grid}: {v.valor}{i < produto.variation!.length - 1 ? ", " : ""}
                                                        </span>
                                                    ))}
                                                </>
                                            )}
                                            {totalProductDiscount > 0 && (
                                                <>
                                                    <br />
                                                    <span style={{ fontSize: "11px", color: "#28a745", fontWeight: "bold" }}>
                                                        Economia: {floatToPrice(totalProductDiscount)}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                        <span className="produto_aq">
                                            {hasPromotion ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
                                                        {floatToPrice(originalPrice * produto.quantity)}
                                                    </span>

                                                    {floatToPrice(finalPrice * produto.quantity)}
                                                </>
                                            ) : (
                                                floatToPrice(originalPrice * produto.quantity)
                                            )}
                                        </span>
                                    </div>
                                );
                            })}

                            <div className="produto" style={{ paddingTop: '8px' }}>
                                <span>Subtotal produtos</span>
                                <span>
                                    {floatToPrice(calculateTotal(products))}
                                </span>
                            </div>

                            <div className="produto">
                                <span>Descontos</span>
                                <span>
                                    -{floatToPrice(calculateTotalDiscount(products))}
                                </span>
                            </div>
                            {getCartDeliveryMethod() && (
                                <div className="produto">
                                    <span>{getCartDeliveryMethod()}</span>
                                    <span>
                                        {floatToPrice(getCartFreight())}
                                    </span>
                                </div>
                            )}


                            <div id="TotalShop" className="total">
                                <span>Total do Carrinho</span>
                                <span>
                                    {floatToPrice(calculateTotal(products) - calculateTotalDiscount(products) + getCartFreight())}
                                </span>
                            </div>
                        </div>
                    </div>

                    <InfoCustomer cart={cart} />

                    {cart.status === "open" && !isExpired && (
                        <div id="compartilhaLink" className="compartilhaLink_s">
                            <div className="compartilhaLinkBotao_s">
                                <button
                                    className="btn textBtn use-cart-highlighted"
                                    onClick={handleUseCart}
                                    style={{
                                        background: colorActiveStatus || "#28a745",
                                        borderColor: colorActiveStatus || "#28a745",
                                        border: 'none'
                                    }}
                                >
                                    ✓ Utilizar Carrinho
                                </button>
                            </div>
                        </div>
                    )}

                    {cart.status === "open" && isExpired && (
                        <div id="compartilhaLink" className="compartilhaLink_s">
                            <div className="compartilhaLinkBotao_s">
                                <button
                                    className="btn textBtn expired-cart-button"
                                    disabled
                                    style={{
                                        border: 'none'
                                    }}
                                >
                                    <FaExclamationTriangle /> Carrinho Expirado
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="order-content">
                <hr className="hr" />
                <p className="data">Data: {datetimeToDateBR(cart.created)}</p>
            </div>
        </div>
    );
}

// Componente InfoCustomer seguindo o padrão do VendorOrderItem
const InfoCustomer: React.FC<{ cart: VendorCartItem }> = ({ cart }) => {
    function customerName() {
        return cart.customerName || 'Cliente não encontrado';
    }

    function customerDoc() {
        const doc = cart.cpfCustomer || '';
        if (!doc) return 'Cliente sem documento';
        const cleaned = doc.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `CPF: ${cpfMask(cleaned)}`;
        } else if (cleaned.length === 14) {
            // Máscara CNPJ: 00.000.000/0000-00
            const cnpjMask = cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
            return `CNPJ: ${cnpjMask}`;
        }
        return 'Documento inválido';
    }

    function customerPhone() {
        if (!cart.phoneCustomer) return '';
        // Remove tudo que não for número
        const cleaned = cart.phoneCustomer.replace(/\D/g, '');
        // Aplica máscara: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
        if (cleaned.length === 11) {
            // Ex: 11987654321 => (11) 98765-4321
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (cleaned.length === 10) {
            // Ex: 1132654321 => (11) 3265-4321
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return cart.phoneCustomer;
    }

    return (
        <div id="clienteDados" className="resumoCliente">
            <div className="clienteDadosTitulo">
                <h3>Dados do Cliente</h3>
            </div>
            <div id="clienteDadosInfo" className="clienteDadosInfo">
                <span>Nome: {customerName()}</span>
                <span>{customerDoc()}</span>
                <span>Telefone: {customerPhone()}</span>
            </div>
        </div>
    );
};